import { MultiTabQuestionnaire } from "@smartintegrity/questionnaire";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useMemo } from "react";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { Col, Row, Spinner } from "reactstrap";
import { createQuestionnaireGapExtension } from "src/modules/data-protection/components/QuestionnaireGapExtension";
import { API_BASE_URL } from "src/modules/data-protection/constants";
import { buildComplianceObligationsMap } from "src/modules/data-protection/helpers/complianceObligations";
import { mapAnswersToSave } from "src/modules/data-protection/helpers/questionnaire";

const QuestionsLoadingPlaceholder = () => {
    return (
        <div>
            <Row className="mb-2">
                <Col lg="12">
                    <p style={{width: '100%', height: '55px'}} className={`dt-field dt-skeleton`}></p>
                </Col>
            </Row>
        </div>
    );
}

const RiskAssessmentQuestionnaire = ({
    questionsQuery,
    t,
    token,
    softwareId,
    setIsCreateAIOpen,
    aiRiskAssessmentId,
    enableQuestionnaire,
    onValidSubmit,
    complianceObligations = []
}) => {
    const updateQuestionnaireMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await axios.post(
                `${API_BASE_URL}/report.update`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            )

            return response.data;
        },
        onSuccess({
            data
        }) {
            toast(t("Report Submitted"), {
                type: 'success'
            });

            onValidSubmit && onValidSubmit(data);
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Error!'), {
                type: "error"
            })
        }
    });

    const handleSubmitQuestionnaire = useCallback((payload) => {
        updateQuestionnaireMutation.mutate({
            questionAnswers: mapAnswersToSave(
                questionsQuery.data?.rawQuestions,
                payload
            ),
            software: softwareId,
            questionnaireType: 'ai_risk_assessment',
            hardware: null,
            reportCase: aiRiskAssessmentId
        })
    }, [
        questionsQuery.data?.rawQuestions,
        updateQuestionnaireMutation,
        softwareId,
        aiRiskAssessmentId
    ]);

    const settings = useMemo(() => {
        let submitButtonSettings = {
            enable: enableQuestionnaire && !updateQuestionnaireMutation.isLoading,
            title: updateQuestionnaireMutation.isLoading ? (
                <>
                    <Spinner 
                        size='sm' 
                        color="white"
                        className="me-1"
                    />
                    {`${t('Saving')}...`}
                </>
            ) : t('Save Changes')
        }

        return {
            tabSettings: {
                showNumber: true,
                showProgressBar: true,
            },
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: ''
            },
            buttonsSettings: {
                submit: {
                    display: true,
                    ...submitButtonSettings
                },
                back: {
                    display: false
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId,
                        file
                    }) => {
                        return `${API_BASE_URL}/attachment/${file.id}/delete`
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId
                    }) => {
                        return `${API_BASE_URL}/report.uploadAttachment`
                    }
                }
            },
            questions: {
                showNumber: true
            }
        }
    }, [
        token,
        t,
        updateQuestionnaireMutation.isLoading,
        enableQuestionnaire
    ]);

    const questionnaireComponents = useMemo(() => {
        const obligationsByCode = buildComplianceObligationsMap(
            complianceObligations
        );
    
        const QuestionExtension = createQuestionnaireGapExtension(
            obligationsByCode
        );

        if (!QuestionExtension) {
            return undefined;
        }

        return {
            QuestionExtension
        };
    }, [complianceObligations]);

    if(questionsQuery.isFetching) {
        return (
            <QuestionsLoadingPlaceholder />
        )
    }

    return (
        <MultiTabQuestionnaire
            questionnaireUniqueId='ai-risk-assessment-questionnaire'
            className='gdpr'
            questions={questionsQuery.data?.mappedQuestions || []}
            defaultValues={questionsQuery.data?.mappedAnswers || []}
            settings={settings}
            components={questionnaireComponents}
            // onQuestionnaireCompleteStatusChanged={}
            onSubmit={handleSubmitQuestionnaire}
        />
    )
}

export default withNamespaces()(
    connect((state) => {
        const { token, user } = state.Login;

        return {
            token,
            user
        };
    })(
        RiskAssessmentQuestionnaire
    )
);